// ============================================================
// 車両ポジションマップの店舗別ゾーン定義
//
// VehiclePositionMap(daylist「車両ポジション」)のゾーン構成は店舗の物理
// レイアウトに依存するため、インスタンス(那覇/宮古)ごとにここで定義する。
// 正(constitution): booking-platform/md/stores.md
//
// statusCode は各店舗 backend の /api/statuses vehicle.code(status_code)と
// 一致させる。実行時に vehicleStatusList から id を解決するので、DB の id
// 採番が変わってもコード変更は不要(動的マスタ方針)。
//
// 【店舗差分の要点】
//   那覇: 赤嶺駅(akamine_south_*)・三井P(mitsui_p)・ヤード(yard)あり。
//         ピックアップサブゾーンに「14番」。
//   返却送迎(送迎車モード): ドロップオフ→ピックアップへ車両を動かすと発動する。
//         対象ゾーンは dropoffZoneIds / pickupZoneIds で店舗別に宣言する。
//   宮古: 赤嶺相当は下地島空港(shimoji_pu/shimoji_do)。三井P・ヤードなし。
//         「14番」相当は「乗降場」。vehicle_location マスタは未整備のため
//         zoneLocationStatus は空(整備後にここへ追記する)。
// ============================================================
import { getInstance } from './instances';

export interface ZoneConfig {
  id: string;
  label: string;
  statusCode: string | null; // null: 状態紐付けなし(予約リストなど)
  gridArea: string;
  color: string;
  borderColor: string;
  columns?: number; // 車両カードの列数(デフォルト: flex wrap)
  droppable?: boolean; // ドロップ可能か(デフォルト: true)
}

/**
 * 返却リストの「連絡」で選ぶ返却の受け方（2026-08-24 追加）。
 *
 * 既定（未選択）= 空港まで送り有り・お客様が利用中の車両で送る。
 * ほとんどがこれなので選択肢には出さず、ここには**例外だけ**を並べる。
 *   赤嶺駅(宮古は下地島) … 空港ではなくそこで降ろす
 *   送迎車               … 利用中の車ではなく送迎車で送る
 *   返却車               … 利用中の車で送る（明示したい時）
 *   返却無               … 送り自体が不要
 *
 * 貸出のピックアップサブゾーンとは別概念なので、保存キーも
 * `返却連絡` / `返却場所` と分けている（同じ予約が貸出日と返却日の
 * 両方でリストに出るため、同じキーを使うと片方が上書きされる）。
 */
export interface ReturnSubZoneConfig {
  id: string;
  label: string;
  value: string; // 予約 json_data.data['返却場所'] に保存される値
  borderColor: string;
  textColor?: string;
}

export interface PickupSubZoneConfig {
  id: string;
  label: string;
  airportValue: string; // 予約 json_data.data['空港'] に保存される値
  borderColor: string;
  textColor?: string;
}

export interface VehicleZonesConfig {
  /** メインマップのゾーン一覧(PC グリッド/モバイル共通) */
  zones: ZoneConfig[];
  /** モバイル時のゾーン表示順序 */
  mobileZoneOrder: string[];
  /**
   * ゾーンごとの移動ステータス **code**(ドロップ時に location_status へセット)。未定義は null。
   * 2026-09-01: id 直書きから code に変更した。移動ステータスの採番も店舗ごとに違うため、
   * 車両ステータスと同じく実行時に /api/statuses の locationStatusList から id を解決する。
   */
  zoneLocationStatus: Record<string, string>;
  /**
   * ピックアップ/ドロップオフ中に「店舗へ戻っている」ことを表す移動ステータス code（→店舗）。
   * 車両ポジションで P.U / D.O の車両カードをダブルクリック（タップ）すると 行き ⇄ 戻り が切り替わる（2026-10-24）
   */
  returnToShopLocationStatus: string;
  /** 当日予約がなくても常時表示するゾーン id(在庫置き場系) */
  alwaysShowZoneIds: string[];
  /** ドロップオフ系ゾーン id(ここから非ドロップオフへ移動時、返却予約の done 確認を出す) */
  dropoffZoneIds: Set<string>;
  /** ピックアップ系ゾーン id(ドロップオフからここへ移動すると返却送迎モードに入る) */
  pickupZoneIds: Set<string>;
  /** ピックアップ(到着連絡済み)内のサブゾーン */
  pickupSubZones: readonly PickupSubZoneConfig[];
  /** 返却リストの「連絡」で選ぶ返却の受け方 */
  returnSubZones: readonly ReturnSubZoneConfig[];
  /** PC グリッドレイアウト */
  grid: { areas: string; columns: string; rows: string };
}

// color はゾーン背景のティント。borderColor(識別色)を白に10%混ぜた実色で、
// DnDの受け皿として領域が判別できる最小限の彩度に抑える(rgba/opacityは使わない)。
const NAHA_ZONES: VehicleZonesConfig = {
  zones: [
    { id: 'airport_1f', label: '空港1Fエリア', statusCode: 'p_u', gridArea: 'f', color: '#fdeee6', borderColor: '#e65100', droppable: false },
    { id: 'akamine_south_pu', label: '赤嶺駅 南口\nピックアップ', statusCode: 'akamine_south_pu', gridArea: 'n', color: '#f7e8ee', borderColor: '#ad1457' },
    { id: 'akamine_south_do', label: '赤嶺駅 南口\nドロップオフ', statusCode: 'akamine_south_do', gridArea: 's', color: '#ebedf7', borderColor: '#3949ab' },
    { id: 'mitsui_p', label: '三井P', statusCode: 'mitsui_p', gridArea: 'm', color: '#eaf2eb', borderColor: '#2e7d32' },
    { id: 'sending', label: '空港3F\n（ドロップオフ）', statusCode: 'd_o', gridArea: 'b', color: '#e8f1fb', borderColor: '#1976d2' },
    { id: 'pickup', label: '空港1F\n（ピックアップ）', statusCode: 'p_u', gridArea: 'c', color: '#f9eaea', borderColor: '#c62828' },
    { id: 'airport', label: '店舗', statusCode: 'airport_store', gridArea: 'a', color: '#fef2e8', borderColor: '#f57f17' },
    { id: 'in_use', label: '利用中\n（返却予定）', statusCode: 'departing', gridArea: 'e', color: '#f2e9f6', borderColor: '#7b1fa2' },
    { id: 'yard', label: 'ヤード', statusCode: 'yard', gridArea: 'd', color: '#edebea', borderColor: '#4e342e' },
  ],
  mobileZoneOrder: ['in_use', 'akamine_south_pu', 'akamine_south_do', 'mitsui_p', 'sending', 'airport', 'pickup', 'airport_1f', 'yard'],
  zoneLocationStatus: {
    pickup: 'to_airport',                 // 空港1F 迎え → 空港←
    sending: 'to_airport',                // 空港3F 送り → 空港←
    akamine_south_pu: 'to_akamine_south', // 赤嶺駅南口P.U → 駅_南←
    akamine_south_do: 'to_akamine_south', // 赤嶺駅南口D.O → 駅_南←
    // 店舗・ヤード・三井P・利用中は移動ステータスを付けない（＝クリア）。
    // 場所を表すエリアは置いたこと自体が「そこにある」意味なので要らない（2026-10-24 前田さん確認。
    // 以前は 店舗=→店舗 / ヤード=→ヤード を付けていた）。移動ステータスは業務（ピックアップ/ドロップオフ）の
    // 中のサブステータスとして使う: 行き=→空港（各エリアの値）、戻り=→店舗（returnToShopLocationStatus）
  },
  returnToShopLocationStatus: 'to_naha_shop',
  alwaysShowZoneIds: ['airport', 'pickup', 'sending', 'yard', 'mitsui_p', 'akamine_south_pu', 'akamine_south_do'],
  dropoffZoneIds: new Set(['sending', 'akamine_south_do']),
  pickupZoneIds: new Set(['pickup', 'akamine_south_pu']),
  pickupSubZones: [
    { id: 'pickup_contact', label: '連絡あり', airportValue: '未', borderColor: '#00695c' },
    { id: 'pickup_lobby', label: 'ロビー', airportValue: 'ロビー', borderColor: '#1565c0' },
    { id: 'pickup_gate14', label: '14番', airportValue: '14番', borderColor: '#5d4037' },
    { id: 'pickup_shuttle', label: '送迎車', airportValue: '送迎車', borderColor: '#e65100' },
    { id: 'pickup_shuttle_return', label: '返却車', airportValue: '返却車', borderColor: '#6d28d9' },
    { id: 'pickup_walkin', label: '直接来店', airportValue: '直接来店', borderColor: '#990700', textColor: '#ffeb3b' },
  ],
  returnSubZones: [
    { id: 'return_akamine', label: '赤嶺駅', value: '赤嶺駅', borderColor: '#ad1457' },
    { id: 'return_shuttle', label: '送迎車', value: '送迎車', borderColor: '#e65100' },
    { id: 'return_car', label: '返却車', value: '返却車', borderColor: '#6d28d9' },
    { id: 'return_none', label: '返却無', value: '返却無', borderColor: '#616161' },
  ],
  grid: {
    areas: `
      "r f b b b b b e"
      "r f c n . s m e"
      "r f c a a a a e"
      "r f d d d d d e"
    `,
    columns: '105px 120px 120px 1fr 30px 1fr 120px 120px',
    rows: 'minmax(80px, auto) auto 1fr auto',
  },
};

// 宮古: 三井P・ヤードなし。赤嶺相当は下地島空港(下地迎/下地送)。
//
// 宮古の vehicle_location マスタ(2026-09-01 に前田さんが登録):
//   to_airport(空港←) / to_naha_shop(→店舗) / to_shimojishima_airport(下地島空港←)
// ※ 「→店舗」の code が to_naha_shop なのは那覇に合わせた命名。宮古店を指す
//    (id も表示名も宮古DBのもの。code だけ那覇と同名)。ヤードは宮古に無い。
const MIYAKO_ZONES: VehicleZonesConfig = {
  zones: [
    { id: 'airport_1f', label: '宮古空港エリア', statusCode: 'p_u', gridArea: 'f', color: '#fdeee6', borderColor: '#e65100', droppable: false },
    { id: 'shimoji_pu', label: '下地島空港\nピックアップ', statusCode: 'shimoji_pu', gridArea: 'n', color: '#f7e8ee', borderColor: '#ad1457' },
    { id: 'shimoji_do', label: '下地島空港\nドロップオフ', statusCode: 'shimoji_do', gridArea: 's', color: '#ebedf7', borderColor: '#3949ab' },
    { id: 'sending', label: '宮古空港\n（ドロップオフ）', statusCode: 'd_o', gridArea: 'b', color: '#e8f1fb', borderColor: '#1976d2' },
    { id: 'pickup', label: '宮古空港\n（ピックアップ）', statusCode: 'p_u', gridArea: 'c', color: '#f9eaea', borderColor: '#c62828' },
    { id: 'airport', label: '店舗', statusCode: 'airport_store', gridArea: 'a', color: '#fef2e8', borderColor: '#f57f17' },
    { id: 'in_use', label: '利用中\n（返却予定）', statusCode: 'departing', gridArea: 'e', color: '#f2e9f6', borderColor: '#7b1fa2' },
  ],
  mobileZoneOrder: ['in_use', 'shimoji_pu', 'shimoji_do', 'sending', 'airport', 'pickup', 'airport_1f'],
  zoneLocationStatus: {
    pickup: 'to_airport',                    // 宮古空港 迎え → 空港←
    sending: 'to_airport',                   // 宮古空港 送り → 空港←
    shimoji_pu: 'to_shimojishima_airport',   // 下地島 迎え → 下地島空港←
    shimoji_do: 'to_shimojishima_airport',   // 下地島 送り → 下地島空港←
    // 店舗は移動ステータスを付けない（那覇と同じ考え方。2026-10-24）
  },
  returnToShopLocationStatus: 'to_naha_shop',
  alwaysShowZoneIds: ['airport', 'pickup', 'sending', 'shimoji_pu', 'shimoji_do'],
  dropoffZoneIds: new Set(['sending', 'shimoji_do']),
  pickupZoneIds: new Set(['pickup', 'shimoji_pu']),
  pickupSubZones: [
    { id: 'pickup_contact', label: '連絡あり', airportValue: '未', borderColor: '#00695c' },
    { id: 'pickup_lobby', label: 'ロビー', airportValue: 'ロビー', borderColor: '#1565c0' },
    { id: 'pickup_platform', label: '乗降場', airportValue: '乗降場', borderColor: '#5d4037' },
    { id: 'pickup_shuttle', label: '送迎車', airportValue: '送迎車', borderColor: '#e65100' },
    { id: 'pickup_shuttle_return', label: '返却車', airportValue: '返却車', borderColor: '#6d28d9' },
    { id: 'pickup_walkin', label: '直接来店', airportValue: '直接来店', borderColor: '#990700', textColor: '#ffeb3b' },
  ],
  returnSubZones: [
    { id: 'return_shimoji', label: '下地島', value: '下地島', borderColor: '#ad1457' },
    { id: 'return_shuttle', label: '送迎車', value: '送迎車', borderColor: '#e65100' },
    { id: 'return_car', label: '返却車', value: '返却車', borderColor: '#6d28d9' },
    { id: 'return_none', label: '返却無', value: '返却無', borderColor: '#616161' },
  ],
  grid: {
    areas: `
      "r f b b b b e"
      "r f c n . s e"
      "r f c a a a e"
    `,
    columns: '105px 120px 120px 1fr 30px 1fr 120px',
    rows: 'minmax(80px, auto) auto 1fr',
  },
};

/** 現在のインスタンスのゾーン設定を返す。 */
export function getVehicleZonesConfig(): VehicleZonesConfig {
  return getInstance() === 'miyako' ? MIYAKO_ZONES : NAHA_ZONES;
}
